import { useEffect, useState } from "react";
import { http } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Download, ExternalLink, Copy, Loader2, FileText, Trash2 } from "lucide-react";

const CHANNELS = [
  { v: "whatnot", l: "Whatnot" },
  { v: "trade_show", l: "Trade Show" },
  { v: "cash", l: "Cash Sale" },
  { v: "facebook_marketplace", l: "Facebook Marketplace" },
  { v: "in_person", l: "In-Person" },
  { v: "gift", l: "Gift" },
  { v: "other", l: "Other" },
];

const FRONTEND = (typeof window !== "undefined") ? window.location.origin : "";
const BACKEND = process.env.REACT_APP_BACKEND_URL || "";

export default function AdminOffSiteSale() {
  const [form, setForm] = useState({
    artwork_title: "",
    print_size: "11x14",
    sale_channel: "whatnot",
    sale_price: "",
    buyer_name: "",
    buyer_email: "",
    city: "",
    state: "",
    country: "USA",
    gift_recipient: "",
    notes: "",
  });
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(true);
  const set = (k, v) => setForm({ ...form, [k]: v });

  const loadHistory = async () => {
    setHistoryLoading(true);
    try {
      // The Admin Registry endpoint returns all truths; we filter to off-site
      // sources client-side and sort by created_at desc, limit 25.
      const { data } = await http.get("/admin/registry?limit=200");
      const offsite = (data || [])
        .filter((t) => t.source === "offsite" || ["whatnot","trade_show","cash","facebook_marketplace","in_person","gift","other"].includes(t.sale_channel))
        .sort((a,b)=> (b.created_at||"").localeCompare(a.created_at||""))
        .slice(0, 25);
      setHistory(offsite);
    } catch (err) {
      console.warn("Failed to load off-site history", err);
    } finally {
      setHistoryLoading(false);
    }
  };

  useEffect(() => { loadHistory(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = async (e) => {
    e.preventDefault();
    if (!form.artwork_title.trim()) {
      toast.error("Artwork title is required.");
      return;
    }
    setBusy(true);
    setResult(null);
    try {
      const body = {
        artwork_title: form.artwork_title.trim(),
        print_size: form.print_size || null,
        sale_channel: form.sale_channel,
        sale_price: form.sale_price ? Number(form.sale_price) : null,
        buyer_name: form.buyer_name || null,
        buyer_email: form.buyer_email || null,
        city: form.city || null,
        state: form.state || null,
        country: form.country || null,
        gift_recipient: form.gift_recipient || null,
        notes: form.notes || null,
      };
      const { data } = await http.post("/admin/offsite-sales", body);
      setResult(data);
      toast.success(`Truth minted — ${data.truth_id}`);
      loadHistory();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Mint failed.");
    } finally {
      setBusy(false);
    }
  };

  const copy = async (txt, label = "Copied") => {
    try { await navigator.clipboard.writeText(txt); toast.success(`${label}.`); }
    catch { toast.error("Copy failed."); }
  };

  return (
    <div className="space-y-6" data-testid="admin-offsite-sale">
      <div>
        <h2 className="font-display text-2xl text-[#F4F1EA] mb-2 flex items-center gap-2">
          <Plus size={18} className="text-[#C9A055]" /> Off-Site Sale Registration
        </h2>
        <p className="font-body text-[13px] text-[#8a857d] max-w-2xl">
          Mint a registry-ready Truth for sales made outside the website
          (Whatnot, trade shows, cash, Facebook Marketplace, gifts, in-person).
          The buyer can claim it later via QR — no account required.
        </p>
      </div>

      <form onSubmit={submit} className="grid lg:grid-cols-2 gap-6">
        <div className="bg-[#0a0a0a] border border-white/10 p-5 space-y-4">
          <Section title="Artwork">
            <Field label="Truth title *">
              <input value={form.artwork_title} onChange={(e) => set("artwork_title", e.target.value)}
                     data-testid="oss-title" className="stc-input" required />
            </Field>
            <Field label="Print size">
              <select value={form.print_size} onChange={(e) => set("print_size", e.target.value)}
                      data-testid="oss-size" className="stc-input">
                <option value="11x14">11×14 Fine Art</option>
                <option value="4x6">4×6 Postcard</option>
                <option value="8x10">8×10</option>
                <option value="16x20">16×20</option>
                <option value="">(custom — not set)</option>
              </select>
            </Field>
          </Section>

          <Section title="Sale">
            <Field label="Channel">
              <select value={form.sale_channel} onChange={(e) => set("sale_channel", e.target.value)}
                      data-testid="oss-channel" className="stc-input">
                {CHANNELS.map((c) => <option key={c.v} value={c.v}>{c.l}</option>)}
              </select>
            </Field>
            <Field label="Sale price (USD)">
              <input type="number" step="0.01" value={form.sale_price}
                     onChange={(e) => set("sale_price", e.target.value)}
                     data-testid="oss-price" className="stc-input" placeholder="optional" />
            </Field>
            {form.sale_channel === "gift" && (
              <Field label="Gift recipient (optional)">
                <input value={form.gift_recipient} onChange={(e) => set("gift_recipient", e.target.value)}
                       data-testid="oss-recipient" className="stc-input"
                       placeholder="If known — they can also claim by QR later" />
              </Field>
            )}
          </Section>
        </div>

        <div className="bg-[#0a0a0a] border border-white/10 p-5 space-y-4">
          <Section title="Buyer (optional — leave blank to mint as UNCLAIMED)">
            <Field label="Buyer name">
              <input value={form.buyer_name} onChange={(e) => set("buyer_name", e.target.value)}
                     data-testid="oss-buyer-name" className="stc-input" />
            </Field>
            <Field label="Buyer email">
              <input type="email" value={form.buyer_email} onChange={(e) => set("buyer_email", e.target.value)}
                     data-testid="oss-buyer-email" className="stc-input" />
            </Field>
            <div className="grid grid-cols-3 gap-2">
              <Field label="City">
                <input value={form.city} onChange={(e) => set("city", e.target.value)}
                       data-testid="oss-city" className="stc-input" />
              </Field>
              <Field label="State">
                <input value={form.state} onChange={(e) => set("state", e.target.value)}
                       data-testid="oss-state" className="stc-input" />
              </Field>
              <Field label="Country">
                <input value={form.country} onChange={(e) => set("country", e.target.value)}
                       data-testid="oss-country" className="stc-input" />
              </Field>
            </div>
          </Section>

          <Section title="Internal notes">
            <textarea value={form.notes} onChange={(e) => set("notes", e.target.value)}
                      rows={3} data-testid="oss-notes" className="stc-input"
                      placeholder="Booth number, transaction reference, anything you'll want later…" />
          </Section>

          <button type="submit" disabled={busy} data-testid="oss-mint"
                  className="btn-gold w-full mt-2 flex items-center justify-center gap-2 disabled:opacity-50">
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
            {busy ? "Minting…" : "Mint Truth & Generate Certificate"}
          </button>
        </div>
      </form>

      {result && (
        <div className="bg-[#0a0a0a] border border-[#C9A055]/40 p-5" data-testid="oss-result">
          <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
            <div>
              <div className="font-ui text-[10px] tracking-[0.32em] uppercase text-[#C9A055] mb-1">
                Truth Minted · status: {result.status}
              </div>
              <div className="font-display text-2xl text-[#F4F1EA]">{result.truth_id}</div>
              <div className="font-ui text-[11px] tracking-[0.22em] uppercase text-[#8a857d] mt-1">
                Claim code: <span className="text-[#C9A055] font-mono">{result.claim_code}</span>
              </div>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <a href={`${BACKEND}${result.certificate_url}`} target="_blank" rel="noreferrer"
               data-testid="oss-cert"
               className="btn-gold flex items-center gap-1.5"><Download size={13} /> Certificate PDF</a>
            <a href={`${FRONTEND}${result.claim_url}`} target="_blank" rel="noreferrer"
               data-testid="oss-claim-link"
               className="btn-outline flex items-center gap-1.5">
              <ExternalLink size={13} /> Open Claim Page
            </a>
            <button onClick={() => copy(`${FRONTEND}${result.claim_url}`, "Claim URL copied")}
                    type="button" data-testid="oss-copy-claim"
                    className="btn-outline flex items-center gap-1.5">
              <Copy size={13} /> Copy Claim URL
            </button>
            <button onClick={() => copy(result.claim_code, "Claim code copied")}
                    type="button" data-testid="oss-copy-code"
                    className="btn-outline flex items-center gap-1.5">
              <Copy size={13} /> Copy Claim Code
            </button>
          </div>
        </div>
      )}

      {/* Recent off-site sales — quick access to certificate / claim / delete */}
      <RecentOffSiteSales rows={history} loading={historyLoading} onChanged={loadHistory} />
    </div>
  );
}

function RecentOffSiteSales({ rows, loading, onChanged }) {
  const onDelete = async (truth_id) => {
    if (!window.confirm(`Permanently delete ${truth_id}? This removes the row entirely. (Use Void instead if you just want to invalidate it.)`)) return;
    try {
      await http.delete(`/admin/registry/${truth_id}`);
      toast.success(`Deleted ${truth_id}`);
      onChanged();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Delete failed.");
    }
  };
  const copy = async (txt, label="Copied") => {
    try { await navigator.clipboard.writeText(txt); toast.success(`${label}.`); }
    catch { toast.error("Copy failed."); }
  };

  return (
    <div className="space-y-3" data-testid="oss-history">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-display text-xl text-[#F4F1EA]">Recent Off-Site Sales</h3>
          <p className="font-body text-[12px] text-[#8a857d]">
            The last 25 truths you minted via off-site sales / gifts / in-person. For full search &amp; edit, use
            the <b>Registry CRUD</b> tab.
          </p>
        </div>
        <button onClick={onChanged} className="btn-outline text-[12px]">Refresh</button>
      </div>

      <div className="border border-white/10 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[#0a0a0a] text-[#8a857d] font-ui text-[9px] tracking-[0.22em] uppercase">
            <tr>
              <th className="text-left p-3">Truth ID</th>
              <th className="text-left p-3">Title</th>
              <th className="text-left p-3">Buyer / Keeper</th>
              <th className="text-left p-3">Channel</th>
              <th className="text-left p-3">Status</th>
              <th className="text-left p-3">Claim</th>
              <th className="text-left p-3">When</th>
              <th className="text-right p-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={8} className="p-6 text-center text-[#8a857d]">
                <Loader2 size={14} className="animate-spin inline-block mr-2"/>Loading…
              </td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={8} className="p-6 text-center text-[#8a857d] italic">No off-site sales minted yet.</td></tr>
            ) : rows.map((t) => (
              <tr key={t.truth_id} className="border-t border-white/5 hover:bg-white/[0.02]"
                  data-testid={`oss-row-${t.truth_id}`}>
                <td className="p-3 font-mono text-[#C9A055]">{t.truth_id}</td>
                <td className="p-3 text-[#F2EFE8]">{t.artwork_title}</td>
                <td className="p-3 text-[#E8E4DC]">
                  {t.steward_display_name || t.owner_display_name || t.buyer_name || "—"}
                  {t.owner_email && <div className="text-[11px] text-[#8a857d]">{t.owner_email}</div>}
                </td>
                <td className="p-3 text-[#B5B0A7] text-[12px]">{t.sale_channel || "—"}</td>
                <td className="p-3 text-[12px] uppercase tracking-[0.18em] text-[#C9A055]">{t.status || "—"}</td>
                <td className="p-3">
                  {t.claim_code ? (
                    <div className="flex items-center gap-1">
                      <code className="text-[#C9A055] font-mono text-[12px]">{t.claim_code}</code>
                      <button onClick={()=>copy(`${FRONTEND}/claim/${t.claim_code}`,"Claim URL copied")}
                        className="text-[#8a857d] hover:text-[#C9A055]" title="Copy claim URL">
                        <Copy size={11}/></button>
                    </div>
                  ) : <span className="text-[#5a554e]">—</span>}
                </td>
                <td className="p-3 text-[12px] text-[#8a857d]">
                  {t.created_at ? new Date(t.created_at).toLocaleDateString() : "—"}
                </td>
                <td className="p-3 text-right">
                  <div className="inline-flex gap-1 flex-wrap justify-end">
                    <button title="Download / Reprint Certificate"
                      data-testid={`oss-cert-${t.truth_id}`}
                      onClick={()=>window.open(`${BACKEND}/api/truths/${t.truth_id}/certificate.pdf?size=letter`, "_blank", "noopener")}
                      className="p-1.5 border border-white/10 rounded-sm text-[#8a857d] hover:border-[#C9A055] hover:text-[#C9A055]">
                      <FileText size={12}/>
                    </button>
                    {t.claim_code && (
                      <a title="Open claim page"
                        data-testid={`oss-open-${t.truth_id}`}
                        href={`/claim/${t.claim_code}`} target="_blank" rel="noreferrer"
                        className="p-1.5 border border-white/10 rounded-sm text-[#8a857d] hover:border-[#C9A055] hover:text-[#C9A055]">
                        <ExternalLink size={12}/>
                      </a>
                    )}
                    <button title="Delete"
                      data-testid={`oss-delete-${t.truth_id}`}
                      onClick={()=>onDelete(t.truth_id)}
                      className="p-1.5 border border-red-700/40 rounded-sm text-red-300 hover:bg-red-900/30">
                      <Trash2 size={12}/>
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <div className="font-ui text-[10px] tracking-[0.22em] uppercase text-[#C9A055] mb-3">{title}</div>
      <div className="space-y-3">{children}</div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="font-ui text-[9px] tracking-[0.22em] uppercase text-[#8a857d] mb-1">{label}</div>
      {children}
    </label>
  );
}
